"use client";

import React, { useState, useMemo, useEffect } from "react";
import { Button, App, Modal } from "antd";
import { useTranslations } from "next-intl";
import Calculator from "@/components/Calculator";
import InquiryButton from "@/components/Inquiry/Button";
import { useShoppingCart } from "@/lib/hooks/useShoppingCart";
import { productTranslationName } from "@/lib/utils/util";
import ContactForm from "@/components/InquryForm/ContactForm";

interface ProductConfigResultProps {
	product: any;
	selectedAttributes: Record<string, string>;
	matchedVariant: any;
	attributeGroups: Array<{ name: string; values: string[] }>;
}

function ProductConfigResult({
	product,
	selectedAttributes,
	matchedVariant,
	attributeGroups,
}: ProductConfigResultProps) {
	const t = useTranslations();
	const { message: antdMessage } = App.useApp();
	const [count, setCount] = useState(1);
	const [cartLoading, setCartLoading] = useState(false);
	const [isInquiryModalOpen, setIsInquiryModalOpen] = useState(false);
	const [downloadLoading, setDownloadLoading] = useState(false);
	const { addToCart } = useShoppingCart();

	// 当 variant 变化时，重置数量为 1
	useEffect(() => {
		setCount(1);
	}, [matchedVariant?.id]);

	// 检查是否所有属性都已配置（用于下载样本等；立即咨询不依赖此项）
	const isAllConfigured = useMemo(() => {
		if (attributeGroups.length === 0) return true;
		return attributeGroups.every((group) => selectedAttributes[group.name]);
	}, [attributeGroups, selectedAttributes]);

	// 询盘兜底：无匹配变体时用首个 SKU，保证未选全参数仍可提交咨询
	const effectiveVariant = useMemo(
		() => matchedVariant ?? product?.variants?.[0] ?? null,
		[matchedVariant, product?.variants],
	);

	// 获取商品标题
	const productTitle = useMemo(() => {
		return productTranslationName(product?.translation?.name) || productTranslationName(product?.name) || "";
	}, [product]);

	// 生成配置型号数组（根据选中的属性，格式：[商品title, 属性值1, 属性值2, ...]）
	const configModelParts = useMemo(() => {
		const parts: string[] = [];
		
		// 第一个是商品title
		if (productTitle) {
			parts.push(productTitle);
		}
		
		// 按照属性组的顺序添加已选择的属性值
		attributeGroups.forEach((group) => {
			const value = selectedAttributes[group.name];
			if (value && value.trim() !== "") {
				parts.push(value);
			} else {
				// 未选择的显示为空
				parts.push("");
			}
		});
		
		return parts;
	}, [productTitle, attributeGroups, selectedAttributes]);

	// 生成配置型号字符串（用于复制）
	const configModel = useMemo(() => {
		return configModelParts.filter(p => p).join(" ");
	}, [configModelParts]);

	// 获取已配置的参数列表（按照左侧属性面板的顺序排序）
	const configuredParams = useMemo(() => {
		// 先按照 attributeGroups 的顺序创建有序的参数列表
		const orderedParams: Array<[string, string]> = [];
		
		// 按照 attributeGroups 的顺序添加参数
		attributeGroups.forEach((group) => {
			const value = selectedAttributes[group.name];
			if (value && value.trim() !== "") {
				orderedParams.push([group.name, value]);
			}
		});
		
		return orderedParams;
	}, [selectedAttributes, attributeGroups]);

	// 复制到剪贴板
	const handleCopyToClipboard = async () => {
		if (!configModel) {
			return antdMessage.warning(t("product.NoConfigModel"));
		}
		try {
			await navigator.clipboard.writeText(configModel);
			antdMessage.success(t("product.CopiedToClipboard"));
		} catch (err) {
			antdMessage.error(t("product.CopyFailed"));
		}
	};

	// 从 metadata 中获取 PDF 文件
	const pdfFiles = useMemo(() => {
		const pdfs: Array<{ url: string; name: string; fileId?: number }> = [];
		
		// 从 product.metadata 中查找 pdf_files
		if (product?.metadata && Array.isArray(product.metadata)) {
			const pdfFilesMeta = product.metadata.find((item: any) => item.key === "pdf_files");
			
			if (pdfFilesMeta && pdfFilesMeta.value) {
				try {
					const pdfFilesArray = JSON.parse(pdfFilesMeta.value);
					
					if (Array.isArray(pdfFilesArray)) {
						pdfFilesArray.forEach((pdfFile: any) => {
							// 检查是否为 PDF 文件（通过 type 或 url/name 扩展名）
							const isPDF = pdfFile.type === "application/pdf" || 
								(pdfFile.url && pdfFile.url.toLowerCase().endsWith(".pdf")) ||
								(pdfFile.name && pdfFile.name.toLowerCase().endsWith(".pdf"));
							
							if (isPDF) {
								// 解码 Unicode 转义字符（如 \u5b50\u8d26\u53f7）
								let fileName = pdfFile.name || "sample.pdf";
								try {
									// 尝试解码 Unicode 转义字符
									fileName = fileName.replace(/\\u([0-9a-fA-F]{4})/g, (match, code) => {
										return String.fromCharCode(parseInt(code, 16));
									});
								} catch (e) {
									// 如果解码失败，使用原始名称
								}
								
								// 如果有 URL 且不为空，使用 URL；否则使用 file_id 构建下载链接
								const url = pdfFile.url && pdfFile.url.trim() !== "" 
									? pdfFile.url 
									: pdfFile.file_id 
										? `${process.env.NEXT_PUBLIC_LANGUAGE_LIST_URL || ""}/medias/file_download/${pdfFile.file_id}`
										: null;
								
								if (url) {
									pdfs.push({
										url: url,
										name: fileName,
										fileId: pdfFile.file_id,
									});
								}
							}
						});
					}
				} catch (error) {
					console.error("Error parsing pdf_files metadata:", error);
				}
			}
		}
		
		return pdfs;
	}, [product]);

	// 下载PDF样本
	const handleDownloadSample = async () => {
		// 防止重复点击
		if (downloadLoading) {
			return;
		}
		
		if (pdfFiles.length === 0) {
			antdMessage.warning(t("product.NoPDFAvailable"));
			return;
		}
		
		setDownloadLoading(true);
		
		// 如果有多个PDF，下载第一个；如果只有一个，直接下载
		const pdfFile = pdfFiles[0];
		
		try {
			const response = await fetch(pdfFile.url);
			if (!response.ok) {
				throw new Error("Failed to download PDF");
			}
			
			const blob = await response.blob();
			const url = URL.createObjectURL(blob);
			
			// 创建下载链接
			const a = document.createElement("a");
			a.href = url;
			a.download = pdfFile.name.endsWith(".pdf") ? pdfFile.name : `${pdfFile.name}.pdf`;
			a.style.display = "none";
			
			// 添加到DOM并触发下载
			document.body.appendChild(a);
			a.click();
			
			// 延迟清理，确保下载开始
			setTimeout(() => {
				document.body.removeChild(a);
				URL.revokeObjectURL(url);
				setDownloadLoading(false);
			}, 100);
			
			antdMessage.success(t("product.DownloadStartedSuccess"));
		} catch (error) {
			console.error("Download error:", error);
			antdMessage.error(t("product.DownloadFailed"));
			setDownloadLoading(false);
		}
	};

	// 打开询盘弹框（未配全参数也可打开）
	const openInquiryModal = () => {
		if (!effectiveVariant) return;
		setIsInquiryModalOpen(true);
	};

	// 关闭询盘弹框
	const closeInquiryModal = () => {
		setIsInquiryModalOpen(false);
	};

	// 获取产品图片
	const getProductImage = () => {
		if (!product?.media) return "/image/default-image2.webp";
		try {
			const media = JSON.parse(product.media);
			return Array.isArray(media) && media.length > 0 ? (media[0] as any)?.url : "/image/default-image2.webp";
		} catch (error) {
			console.error("Error parsing product media:", error);
			return "/image/default-image2.webp";
		}
	};

	// 获取当前产品的询盘数据（包含数量、属性等信息；参数未选全时 attributes 可为空或部分）
	const getCurrentProductInquiry = useMemo(() => {
		if (!product || !effectiveVariant) return [];

		const attributeInfo = configuredParams.map(([key, value]) => `${key}: ${value}`).join(", ");

		return [
			{
				variant: {
					product: product,
					id: effectiveVariant.id,
					name: effectiveVariant.name || productTitle,
				},
				name: productTitle,
				slug: product.slug,
				quantity: count,
				attributes: attributeInfo,
			},
		];
	}, [product, effectiveVariant, productTitle, count, configuredParams]);

	return (
		<div className="w-full overflow-hidden">
			{/* 配置型号/订货号 */}
			<div className="px-6 py-4 border-b border-gray-200">
				<div className="flex items-center justify-between gap-4 flex-wrap">
					<div className="flex-1 min-w-[200px]">
					<div className="flex items-center gap-3 mb-6">
						<div className="w-1 h-8 bg-mainColor"></div>
						<h2 className="max-md:text-lg text-3xl font-semibold text-gray-800">
							{t("product.ConfigurationModel")}
						</h2>
					</div>
						<div className="flex items-center gap-0 flex-wrap">
							<div className="flex items-center gap-1 flex-wrap">
								{configModelParts.map((part, index) => (
									<div 
										key={index}
										className={`text-lg max-md:text-base font-mono px-3 py-2 h-10 flex items-center justify-center min-w-[60px] ${
											part 
												? "bg-mainColor/5 text-gray-900" 
												: "bg-mainColor/5 text-gray-300"
										}`}
									>
										{part || ""}
									</div>
								))}
							</div>
							{productTitle && (
								<>
									<div
										onClick={handleCopyToClipboard}
										className=" !text-mainColor hover:!text-red-800 !flex items-center gap-1 pl-3 cursor-pointer"
									>
										<i className="ri-file-copy-line"></i>
										{t("product.CopyToClipboard")}
									</div>
								</>
							)}
						</div>
					</div>
				</div>
			</div>

			{/* 已配置参数 */}
			<div className="px-6 py-4">
				<h3 className="text-lg text-gray-800 mb-4 flex items-center gap-2">
					<span>↓</span>
					{t("product.ConfiguredParameters")}
				</h3>
				{configuredParams.length > 0 ? (
					<div className="space-y-0">
						{configuredParams.map(([key, value], index) => (
							<div key={key} className="border-b border-gray-200">
								<div className="flex items-start text-sm leading-relaxed py-2 px-3 hover:bg-gray-50 transition-colors duration-200 cursor-default">
									<span className="font-medium text-gray-900 min-w-[160px] pr-4">{key}:</span>
									<span className="text-gray-700 flex-1 italic">{value}</span>
								</div>
							</div>
						))}
					</div>
				) : (
					<div className="text-sm text-gray-400 italic">
						{t("product.NoParametersConfigured")}
					</div>
				)}
			</div>

			{/* 操作按钮区域 */}
			<div className="px-6 py-4 bg-white">
				{/* 未完成配置提示 */}
				{!isAllConfigured && (
					<div className="bg-sky-50 border border-sky-200 rounded-md p-3 mb-4">
						<p className="text-sm text-sky-900">
							{t("product.ConfigurationIncompleteHint")}
						</p>
					</div>
				)}

				{/* 按钮组 - 一行排列 */}
				<div className="flex items-center gap-4 flex-wrap">
					{/* 数量选择器 */}
					<div className="flex items-center gap-3">
						{/* <span className="text-sm font-medium text-gray-700">{t("product.Quantity")}:</span> */}
						<Calculator
							initCount={1}
							className="h-[36px] w-[70px]"
							changeCount={setCount}
							disabled={false}
							minCount={1}
							maxCount={matchedVariant?.quantityAvailable || 999}
						/>
					</div>

					{/* 询盘按钮：未配全参数也可提交咨询 */}
					<div
						onClick={effectiveVariant ? openInquiryModal : undefined}
						className={`!font-poppins flex items-center justify-center border-[1px] !rounded-md  !px-4 !py-2.5 text-base transition-colors ${
							effectiveVariant
								? "cursor-pointer border-mainColor hover:bg-mainColor !text-gray-800 group"
								: "border-gray-300 !text-gray-400 !cursor-not-allowed"
						}`}
					>
						<span className={`flex items-center gap-2 ${effectiveVariant ? "group-hover:text-white" : ""}`}>
							<i
								className={`ri-message-line ${effectiveVariant ? "text-mainColor group-hover:text-white" : "text-gray-400"}`}
							></i>
							{t("product.InquiryNow")}
						</span>
					</div>

					{/* 下载样本按钮 */}
					{pdfFiles.length > 0 && isAllConfigured ? (
						<div
							onClick={handleDownloadSample}
							className={`!font-poppins flex items-center justify-center bg-mainColor text-white !rounded-md !px-4 !py-2.5 text-base transition-colors ${
								downloadLoading 
									? "!text-gray-400 !cursor-not-allowed opacity-50" 
									: "cursor-pointer hover:!bg-mainColor/80"
							}`}
						>
							<span className="flex items-center gap-2">
								{downloadLoading ? (
									<svg className="h-4 w-4 animate-spin text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
										<circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
										<path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
									</svg>
								) : (
									<i className="ri-download-line text-white"></i>
								)}
								{t("product.SampleDownload")}
							</span>
						</div>
					) : (
						<div className="!font-poppins flex items-center justify-center bg-mainColor/50 text-white !rounded-md !px-4 !py-2.5 text-base !cursor-not-allowed">
							<span className="flex items-center gap-2">
								<i className="ri-download-line text-white"></i>
								{t("product.SampleDownload")}
							</span>
						</div>
					)}
				</div>
			</div>

			{/* 询盘弹框 */}
			<Modal
				title={null}
				open={isInquiryModalOpen}
				onCancel={closeInquiryModal}
				footer={null}
				width={800}
				centered
				destroyOnClose
				className="modal-inquiry"
				styles={{
					body: {
						padding: 0,
					},
				}}
			>
				<div className="relative box-border !overflow-y-auto p-8">
					{/* 标题 */}
					<div className="mb-6">
						<h2 className="text-2xl max-md:text-xl font-bold text-gray-900">{t("form.RequestAQuote")}</h2>
					</div>

					{/* 显示询盘产品信息 */}
					{product && effectiveVariant && (
						<div className="mb-6 rounded-lg border border-gray-200 bg-gray-50 shadow-sm overflow-hidden">
							{/* 标题栏 */}
							<div className="bg-white border-b border-gray-200 px-5 py-3">
								<h3 className="text-lg font-semibold text-gray-900">{t("product.InquiryProduct")}</h3>
							</div>
							
							{/* 产品信息内容 */}
							<div className="p-5">
								<div className="flex items-start gap-5">
									{/* 产品图片 */}
									<div className="flex-shrink-0">
										<div className="w-24 h-24 bg-white rounded-lg border border-gray-200 p-2 flex items-center justify-center">
											<img
												src={getProductImage()}
												alt={productTitle}
												className="w-full h-full object-contain rounded"
											/>
										</div>
									</div>
									
									{/* 产品详情 */}
									<div className="flex-1 min-w-0">
										{/* 产品名称 */}
										<h4 className="text-base font-semibold text-gray-900 mb-3 leading-tight">
											{productTitle}
										</h4>
										
										{/* 数量 */}
										<div className="mb-2">
											<span className="text-sm font-medium text-gray-700">
												{t("product.Quantity")}: 
											</span>
											<span className="text-sm text-gray-900 ml-2 font-semibold">
												{count}
											</span>
										</div>
										
										{/* 已配置参数 */}
										{configuredParams.length > 0 && (
											<div className="mt-2">
												<span className="text-sm font-medium text-gray-700">
													{t("product.ConfiguredParameters")}: 
												</span>
												<span className="text-sm text-gray-900 ml-2">
													{configuredParams.map(([key, value], index) => (
														<span key={key}>
															{index > 0 && <span className="mx-2 text-gray-400">|</span>}
															<span>{key}: {value}</span>
														</span>
													))}
												</span>
											</div>
										)}
									</div>
								</div>
							</div>
						</div>
					)}

					{/* 询盘表单 */}
					<div className="mt-6">
						<ContactForm
							locale={product?.locale || "en"}
							className="!py-0"
							inquiry={getCurrentProductInquiry}
							source="product-modal-inquiry"
						/>
					</div>
				</div>
			</Modal>
		</div>
	);
}

export default React.memo(ProductConfigResult);
